import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  colors,
  Heading,
  Text,
  VStack,
} from "@biblioteksentralen/react";
import { FrontendLocale } from "@libry-content/localization";
import addDays from "date-fns/addDays";
import getMonth from "date-fns/getMonth";
import setMonth from "date-fns/setMonth";
import { capitalize, isEmpty, range, shift, sort } from "radash";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { withErrorBoundary } from "../../errorPages/withErrorBoundary";
import { DateHelper } from "@libry-content/common";
import { OpeningHoursHelper, SpecialHoursList } from "../openingHoursHelper/openingHoursHelper";
import SpecialOpeningHoursMonth from "./SpecialOpeningHoursMonth";

const getCurrentMonth = () => new Date().getMonth();

const getMonthNumbersOrderedFromNow = () => shift(Array.from(range(0, 11)), -getCurrentMonth());

// Assumes that we are not looking more than 11 months ahead
const sortByMonth = (groupedArray: [string, SpecialHoursList][]) =>
  sort(groupedArray, ([monthString]) => getMonthNumbersOrderedFromNow().indexOf(Number(monthString)));

const monthNumberToLabel = (code: FrontendLocale, monthNumber: number): string =>
  capitalize(DateHelper.getLabel(code, setMonth(new Date(), monthNumber), "LLLL"));

export const SpecialOpeningHoursList = ({ openingHoursHelper }: { openingHoursHelper: OpeningHoursHelper }) => {
  const { t, lang } = useTranslation();
  const today = new Date();
  const sevenDaysFromNow = addDays(today, 7);
  const currentMonthNumber = getMonth(today);
  const openingHoursGroupedByMonth = openingHoursHelper.specialOpeningHoursGroupedByMonth(sevenDaysFromNow);

  if (Object.values(openingHoursGroupedByMonth).every(isEmpty)) return null;

  return (
    <VStack as="section" align="start">
      <Heading as="h2" size="md" marginBottom="0.75rem">
        {t("Avvikende åpningstider")}
      </Heading>
      <Accordion as="ol" listStyleType="none" width="100%" reduceMotion allowMultiple>
        {sortByMonth(Object.entries(openingHoursGroupedByMonth)).map(([monthString, specialOpeningHoursForMonth]) => {
          const monthNumber = Number(monthString);
          const isCurrentMonth = currentMonthNumber === monthNumber;

          if (isCurrentMonth) {
            return (
              <AccordionItem as="li" key={monthNumber} border="none">
                <SpecialOpeningHoursMonth key={monthNumber} specialOpeningHours={specialOpeningHoursForMonth} />
              </AccordionItem>
            );
          }

          return (
            <AccordionItem as="li" key={monthNumber} border="none">
              <Heading as="h3" borderTop={`1px solid ${colors.grey15}`}>
                <AccordionButton boxShadow="none !important" padding="0.75rem 0.75rem">
                  <Text fontWeight={600}>{monthNumberToLabel(lang, monthNumber)}</Text>
                  <AccordionIcon />
                </AccordionButton>
              </Heading>
              <AccordionPanel padding="0">
                <SpecialOpeningHoursMonth specialOpeningHours={specialOpeningHoursForMonth} />
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </VStack>
  );
};

export default withErrorBoundary(SpecialOpeningHoursList, "SpecialOpeningHoursList");
